import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import apiService from '@/api/service'
import type { StoreAnalyzeResponse, Comment } from '@/types/api'
import { ReplyModal } from '@/components/ReplyModal'

export const DashboardContent = (): JSX.Element => {
    const navigate = useNavigate()
    const [data, setData] = useState<StoreAnalyzeResponse | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string>('')
    const [isReplyModalOpen, setIsReplyModalOpen] = useState(false)
    const [selectedComment, setSelectedComment] = useState<Comment | null>(null)

    useEffect(() => {
        // 使用 AbortController 來處理組件卸載時的清理
        const abortController = new AbortController()
        let isMounted = true

        const fetchData = async () => {
            try {
                // 使用真實 API 抓取資料
                const storeCode = apiService.getCurrentStoreCode()
                const token = apiService.getCurrentToken()

                if (!storeCode || !token) {
                    console.warn('未找到 store_code 或 token，跳轉到登入頁面')
                    navigate('/login')
                    return
                }

                console.log('正在從 API 獲取店家分析資料（近期概況）...')
                const response = await apiService.getStoreAnalyze({
                    store_code: storeCode,
                    argameusertoken: token
                })

                // 檢查組件是否已卸載
                if (!isMounted || abortController.signal.aborted) {
                    console.log('組件已卸載，取消數據更新')
                    return
                }

                console.log('店家分析資料獲取成功（近期概況）:', response)

                // 檢查是否為 token 失效錯誤
                if (response && response.status === 'failed' &&
                    response.message && response.message.includes('This tokens is invalid, please login again')) {
                    console.warn('Token 已失效，清除登入資料並跳轉到登入頁面')

                    // 清除所有登入相關資料
                    apiService.logout()

                    // 跳轉到登入頁面
                    navigate('/login')
                    return
                }

                // 驗證資料結構
                if (!response || !response.data || !response.data.overview) {
                    console.error('API 返回的資料結構不正確:', response)
                    throw new Error('API 返回的資料結構不完整，缺少 data.overview')
                }

                // 如果 API 返回了店家資訊，保存到 localStorage
                if (response.data.store) {
                    console.log('儲存店家資訊:', response.data.store)
                    apiService.saveStoreInfo(response.data.store)
                }

                setData(response)

                /* ===== 測試資料（已停用）===== */
                // const mockData: StoreAnalyzeResponse = {
                //     message: '',
                //     status: 'success',
                //     timestamp: '2025-09-30T10:25:40.945000',
                //     data: {
                //         storeName: '美味餐廳',
                //         overview: {
                //             commentsTotal: 256,
                //             commentsMoM: 15,
                //             monthIncreased: 12,
                //             increasedMoM: 20,
                //             monthNotReply: 5,
                //             comments: [
                //                 {
                //                     username: '洪XX',
                //                     rating: '1.0',
                //                     relative_date: '2025-02-03T12:30:20.945000',
                //                     caption: '去用餐，後位時寫的表單送的餐根本沒送上桌吃飽以後才想到⋯⋯',
                //                     isRead: 'false'
                //                 },
                //                 {
                //                     username: '洪XX',
                //                     rating: '1.0',
                //                     relative_date: '2025-02-03T12:30:20.945000',
                //                     caption: '去用餐，後位時寫的表單送的餐根本沒送上桌吃飽以後才想到⋯⋯',
                //                     isRead: 'false'
                //                 },
                //                 {
                //                     username: '洪XX',
                //                     rating: '1.0',
                //                     relative_date: '2025-02-03T12:30:20.945000',
                //                     caption: '去用餐，後位時寫的表單送的餐根本沒送上桌吃飽以後才想到⋯⋯',
                //                     isRead: 'false'
                //                 }
                //             ]
                //         },
                //         commentsAnalyze: {
                //             category: {
                //                 positive: [
                //                     { name: '食材新鮮度', specificGravity: 72 },
                //                     { name: '餐點口感', specificGravity: 55 },
                //                     { name: '環境舒適度', specificGravity: 80 },
                //                     { name: '菜單多樣性', specificGravity: 72 },
                //                     { name: '等候時間', specificGravity: 72 }
                //                 ],
                //                 negative: [
                //                     { name: '食材新鮮度', specificGravity: 28 },
                //                     { name: '餐點口感', specificGravity: 45 },
                //                     { name: '環境舒適度', specificGravity: 20 },
                //                     { name: '菜單多樣性', specificGravity: 28 },
                //                     { name: '等候時間', specificGravity: 28 }
                //                 ]
                //             },
                //             keywords: {
                //                 positive: [
                //                     { name: '新鮮', commentsNum: 72 },
                //                     { name: '美味', commentsNum: 55 },
                //                     { name: '舒適', commentsNum: 80 },
                //                     { name: '多樣', commentsNum: 72 },
                //                     { name: '快速', commentsNum: 72 }
                //                 ],
                //                 negative: [
                //                     { name: '不新鮮', commentsNum: 28 },
                //                     { name: '不美味', commentsNum: 45 },
                //                     { name: '不舒適', commentsNum: 20 },
                //                     { name: '單調', commentsNum: 28 },
                //                     { name: '慢', commentsNum: 28 }
                //                 ]
                //             },
                //             average: {
                //                 comments: [
                //                     { rating: 1.0, specificGravity: 2 },
                //                     { rating: 2.0, specificGravity: 3 },
                //                     { rating: 3.0, specificGravity: 17 },
                //                     { rating: 4.0, specificGravity: 23 },
                //                     { rating: 5.0, specificGravity: 55 }
                //                 ],
                //                 sentiment: {
                //                     positive: 75,
                //                     negative: 25
                //                 }
                //             },
                //             lineChart: {
                //                 positive: [
                //                     { sum: 8 },
                //                     { sum: 7 },
                //                     { sum: 6 },
                //                     { sum: 5 },
                //                     { sum: 9 },
                //                     { sum: 6 },
                //                     { sum: 3 }
                //                 ],
                //                 negative: [
                //                     { sum: 4 },
                //                     { sum: 5 },
                //                     { sum: 4 },
                //                     { sum: 3 },
                //                     { sum: 2 },
                //                     { sum: 2 },
                //                     { sum: 3 }
                //                 ]
                //             }
                //         }
                //     }
                // }
                // console.log('使用 API 文件中的測試資料')
                // setData(mockData)
                /* ===== 測試資料結束 ===== */
            } catch (error) {
                // 檢查是否是因為組件卸載而中止
                if (!isMounted || abortController.signal.aborted) {
                    console.log('請求已中止')
                    return
                }

                console.error('Failed to fetch store analyze:', error)
                const errorMessage = error instanceof Error ? error.message : '載入資料失敗'
                setError(errorMessage)
            } finally {
                if (isMounted && !abortController.signal.aborted) {
                    setIsLoading(false)
                }
            }
        }

        fetchData()

        // 清理函數：組件卸載時執行
        return () => {
            console.log('DashboardContent 組件卸載，清理資源')
            isMounted = false
            abortController.abort()
        }
    }, [navigate])

    if (isLoading) {
        return (
            <div className="inline-flex flex-col max-w-[1200px] h-[934px] items-center justify-center bg-white rounded-[0px_20px_20px_20px]">
                <div className="text-[#373737] text-xl">載入中...</div>
            </div>
        )
    }

    if (error || !data) {
        return (
            <div className="inline-flex flex-col max-w-[1200px] h-[934px] items-center justify-center bg-white rounded-[0px_20px_20px_20px]">
                <div className="text-red-500 text-xl">{error || '無法載入資料'}</div>
            </div>
        )
    }

    const { overview } = data.data

    // 處理點擊回覆評論按鈕
    const handleReplyClick = (comment: Comment) => {
        setSelectedComment(comment)
        setIsReplyModalOpen(true)
    }

    // 關閉彈窗
    const handleCloseModal = () => {
        setIsReplyModalOpen(false)
        setSelectedComment(null)
    }

    return (
        <>
            {/* 回覆彈窗 */}
            <ReplyModal
                isOpen={isReplyModalOpen}
                onClose={handleCloseModal}
                comment={selectedComment}
            />

            {/* 主內容 - 响应式 */}
            <div className="inline-flex flex-col w-full max-w-[95%] md:max-w-[1200px] min-h-[600px] md:min-h-[1050px] items-start gap-8 md:gap-[50px] pt-0 pb-6 md:pb-[30px] px-4 md:px-0 relative bg-white rounded-[0px_20px_20px_20px]">
                {/* 統計卡片 - 响应式 */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-5 px-0 md:px-[145px] pt-8 md:pt-12 relative self-stretch w-full flex-[0_0_auto]">
                    {/* 評論總數 - 响应式 */}
                    <div className="relative w-full md:w-[280px] h-[160px] md:h-[190px] bg-[#aeceb7] rounded-[15px] md:rounded-[20px] overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-[60px] md:h-[70px] flex items-center justify-center">
                            <div className="text-white text-xl md:text-2xl text-center tracking-[1.80px] md:tracking-[2.40px] leading-4 [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                                評論總數
                            </div>
                        </div>

                        <div className="absolute top-[60px] md:top-[75px] left-1/2 -translate-x-1/2 [font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-4xl md:text-5xl text-center tracking-[3.60px] md:tracking-[4.80px] leading-4 whitespace-nowrap">
                            {overview.commentsTotal}
                        </div>

                        <div className="absolute top-[70px] md:top-[85px] left-4 md:left-[22px] text-white text-sm md:text-base text-center tracking-[1.20px] md:tracking-[1.60px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            共
                        </div>

                        <div className="absolute top-[70px] md:top-[85px] right-4 md:right-[38px] text-white text-sm md:text-base text-center tracking-[1.20px] md:tracking-[1.60px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            則
                        </div>

                        <p className="absolute bottom-4 md:top-[140px] left-1/2 -translate-x-1/2 text-transparent text-[1rem] text-center tracking-[1.00px] md:tracking-[1.40px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            <span className="text-[#8a8387] tracking-[0.20px]">
                                近30日增加{' '}
                            </span>
                            <span className="text-[#f04c54] tracking-[0.20px]">
                                {overview.commentsMoM > 0 ? '+' : ''}{String(overview.commentsMoM).replace(/%/g, '')}%
                            </span>
                        </p>
                    </div>

                    {/* 近30日新增評論數 - 响应式 */}
                    <div className="relative w-full md:w-[280px] h-[160px] md:h-[190px] bg-[#e8d080] rounded-[15px] md:rounded-[20px] overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-[60px] md:h-[70px] flex items-center justify-center">
                            <div className="text-white text-lg md:text-2xl text-center tracking-[1.60px] md:tracking-[2.40px] leading-5 md:leading-4 [font-family:'Noto_Sans_TC',Helvetica] font-bold px-2">
                                近30日新增評論數
                            </div>
                        </div>

                        <div className="absolute top-[60px] md:top-[75px] left-1/2 -translate-x-1/2 [font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-4xl md:text-5xl text-center tracking-[3.60px] md:tracking-[4.80px] leading-4 whitespace-nowrap">
                            {overview.monthIncreased}
                        </div>

                        <div className="absolute top-[70px] md:top-[85px] left-4 md:left-6 text-white text-sm md:text-base text-center tracking-[1.20px] md:tracking-[1.60px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            新增
                        </div>

                        <div className="absolute top-[70px] md:top-[85px] right-4 md:right-[38px] text-white text-sm md:text-base text-center tracking-[1.20px] md:tracking-[1.60px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            則
                        </div>

                        <p className="absolute bottom-4 md:top-[138px] left-1/2 -translate-x-1/2 text-transparent text-[1rem] text-center tracking-[1.00px] md:tracking-[1.40px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            <span className="text-[#8a8387] tracking-[0.20px]">成長 </span>
                            <span className="text-[#f04c54] tracking-[0.20px]">
                                {overview.increasedMoM > 0 ? '+' : ''}{String(overview.increasedMoM).replace(/%/g, '')}%
                            </span>
                        </p>
                    </div>

                    {/* 近30日尚未回覆 - 响应式 */}
                    <div className="relative w-full md:w-[280px] h-[160px] md:h-[190px] bg-[#aeceb7] rounded-[15px] md:rounded-[20px] overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-[60px] md:h-[70px] flex items-center justify-center">
                            <div className="text-white text-lg md:text-2xl text-center tracking-[1.60px] md:tracking-[2.40px] leading-5 md:leading-4 [font-family:'Noto_Sans_TC',Helvetica] font-bold px-2">
                                近30日尚未回覆
                            </div>
                        </div>

                        {/* 数字和文字容器 */}
                        <div className="absolute top-[60px] md:top-[75px] left-1/2 -translate-x-1/2 flex items-center justify-center gap-2">
                            <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-4xl md:text-5xl text-center tracking-[3.60px] md:tracking-[4.80px] leading-none whitespace-nowrap">
                                {overview.monthNotReply}
                            </div>
                            <div className="text-white text-sm md:text-base text-center tracking-[1.20px] md:tracking-[1.60px] leading-none whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold mt-2">
                                則待回覆
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('/reviewcenter')}
                            className="absolute bottom-4 md:top-[131px] left-1/2 -translate-x-1/2 w-[160px] md:w-[180px] h-[28px] md:h-[30px] flex items-center justify-center bg-[#e8d080] rounded-[50px] md:rounded-[99px] overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        >
                            <div className="text-white text-xs md:text-sm text-center tracking-[1.00px] md:tracking-[1.40px] leading-4 [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                                進入回覆中心
                            </div>
                        </button>
                    </div>
                </div>

                {/* 待回覆清單標題 - 响应式 */}
                <div className="flex w-full md:w-[1200px] items-center gap-4 md:gap-[500px] px-4 md:px-[140px] pt-3 md:pt-[15px] pb-0 relative flex-[0_0_auto]">
                    <div className="relative flex items-center gap-2 md:gap-0 md:w-[283.01px] h-[30px] md:h-[35px]">
                        <img
                            className="w-[30px] h-[30px] md:w-[35px] md:h-[35px] aspect-[1]"
                            alt="File dock fill"
                            src="/img/file-dock-fill.svg"
                        />

                        <div className="text-[#373737] text-lg md:text-2xl tracking-[1.80px] md:tracking-[2.40px] leading-[normal] [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            待回覆清單
                        </div>
                    </div>
                </div>

                {/* 評論列表 - 响应式 */}
                <div className="flex flex-col w-full md:w-[883px] items-center gap-4 md:gap-5 px-4 md:px-[45px] py-4 md:py-6 relative md:absolute md:top-[calc(50.00%_-_150px)] md:left-[calc(50.00%_-_441px)] bg-[#eaeade] rounded-[15px] md:rounded-[20px] overflow-hidden border-2 md:border-[3px] border-solid border-white">
                    {overview.comments.slice(0, 3).map((comment, index) => {
                        // 將評分轉換為數字，確保正確顯示星級
                        const rating = parseFloat(comment.rating) || 0

                        return (
                            <div
                                key={index}
                                className="flex flex-col md:flex-row items-stretch relative self-stretch w-full flex-[0_0_auto] bg-white rounded-[10px] md:rounded-[11.72px] overflow-hidden"
                            >
                                <div className="flex flex-col w-full md:w-[620px] items-start relative flex-1 md:pr-5">
                                    {/* 分隔线 */}
                                    <div className="hidden md:block absolute top-0 right-0 w-[1px] h-full bg-[#929084]"></div>
                                    {/* 用户信息区域 - 固定高度，不会被压缩 */}
                                    <div className="relative self-stretch w-full h-[60px] md:h-[65px] flex-shrink-0">
                                        <div className="absolute top-[calc(50.00%_-_21px)] left-[22px] w-11 h-11 flex items-center justify-center">
                                            <div className="mt-[0.6px] h-[29.3px] ml-[0.6px] w-[29.3px] bg-[#d9d9d9] rounded-[14.65px]" />
                                        </div>

                                        <div className="absolute top-[calc(50.00%_-_21px)] left-[73px] w-[138px] h-5 flex items-center">
                                            <div className="flex items-center justify-start mt-[-0.5px] h-3 w-[93px] [font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#1a1a1a] text-[14px] tracking-[1.00px] leading-[normal] whitespace-nowrap truncate">
                                                {comment.username}
                                            </div>
                                        </div>

                                        <div className="absolute top-[50.00%] left-[73px] w-[204px] h-5">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <img
                                                    key={star}
                                                    className={`absolute top-[5px] w-[11px] h-[11px]`}
                                                    style={{ left: `${(star - 1) * 14 + 6}px` }}
                                                    alt="Star"
                                                    src={
                                                        star <= rating
                                                            ? "/img/star-3-8.svg"
                                                            : "/img/star-4-8.svg"
                                                    }
                                                />
                                            ))}

                                            <div className="absolute top-1.5 left-[calc(50.00%_-_18px)] w-[120px] h-2.5 flex items-center justify-center text-[#999898] text-[8px] tracking-[0] leading-[normal] [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                                                {comment.relative_date}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 评论内容区域 - 可以扩展，确保有足够空间显示3行 */}
                                    <div className="flex items-start gap-2.5 px-4 md:px-[22px] pt-0 md:pt-1 pb-3 md:pb-[15px] relative self-stretch w-full min-h-[60px] md:min-h-[70px]">
                                        <div className="relative w-full md:w-[564px] text-black text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] leading-relaxed md:leading-[1.5] [font-family:'Noto_Sans_TC',Helvetica] font-bold line-clamp-3 break-words overflow-hidden">
                                            {comment.caption}
                                        </div>
                                    </div>
                                </div>

                                {/* 回复按钮区域 - 固定宽度，垂直居中 */}
                                <div className="flex items-center md:items-center px-4 md:pl-5 py-3 md:py-0 relative w-full md:w-auto md:flex-shrink-0 justify-center border-t md:border-t-0 border-[#929084]">
                                    <button
                                        onClick={() => handleReplyClick(comment)}
                                        className="relative w-full max-w-[200px] md:w-[110px] h-[35px] md:h-[37px] bg-[#aeceb7] rounded-[5px] overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                                    >
                                        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 flex items-center justify-center text-black text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] leading-[normal] whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                                            回覆評論
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )
                    })}

                    <button
                        onClick={() => navigate('/reviewcenter')}
                        className="relative w-full max-w-[200px] md:w-[180px] h-[45px] bg-[#e8d080] rounded-[50px] md:rounded-[99px] overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    >
                        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 text-white text-xs md:text-sm text-center tracking-[1.00px] md:tracking-[1.40px] leading-4 whitespace-nowrap [font-family:'Noto_Sans_TC',Helvetica] font-bold">
                            更多評論
                        </div>
                    </button>
                </div>
            </div>
        </>
    )
}
